import { useGetIdentity, useRecordContext, useTranslate } from "ra-core";
import { DataTable } from "@/components/admin/data-table";
import { DateField } from "@/components/admin/date-field";
import { List } from "@/components/admin/list";
import { SearchInput } from "@/components/admin/search-input";
import { SelectInput } from "@/components/admin/select-input";
import { ToggleFilterButton } from "@/components/admin/toggle-filter-button";

import type { Lead } from "../types";
import { ClaimLeadButton } from "./ClaimLeadButton";
import { LeadStatusBadge } from "./LeadStatusBadge";
import { ResponseTime } from "./ResponseTime";
import { formatDuration, LEAD_SOURCES, LEAD_STATUSES } from "./leadUtils";

/** Schnellfilter: nur offene oder die eigenen Leads. */
const InboxFilter = () => {
  const { identity } = useGetIdentity();
  return (
    <div className="flex gap-1">
      <ToggleFilterButton
        className="w-auto"
        label="resources.leads.filter.open"
        value={{ status: "neu" }}
      />
      <ToggleFilterButton
        className="w-auto"
        label="resources.leads.filter.mine"
        value={{ sales_id: identity?.id }}
      />
    </div>
  );
};

const filters = [
  <SearchInput source="q" alwaysOn />,
  <SelectInput
    source="status"
    choices={LEAD_STATUSES.map((value) => ({
      id: value,
      name: `resources.leads.status.${value}`,
    }))}
  />,
  <SelectInput
    source="source"
    choices={LEAD_SOURCES.map((value) => ({
      id: value,
      name: `resources.leads.source.${value}`,
    }))}
  />,
];

const StatusCell = () => {
  const lead = useRecordContext<Lead>();
  if (!lead) return null;
  return <LeadStatusBadge status={lead.status} />;
};

const SourceCell = () => {
  const lead = useRecordContext<Lead>();
  const translate = useTranslate();
  if (!lead) return null;
  return (
    <div className="flex flex-col">
      <span>{translate(`resources.leads.source.${lead.source}`)}</span>
      {lead.campaign ? (
        <span className="text-xs text-muted-foreground">{lead.campaign}</span>
      ) : null}
    </div>
  );
};

const NameCell = () => {
  const lead = useRecordContext<Lead>();
  if (!lead) return null;
  return (
    <div className="flex flex-col">
      <span className="font-medium">
        {[lead.first_name, lead.last_name].filter(Boolean).join(" ") || "–"}
      </span>
      <span className="text-xs text-muted-foreground">{lead.phone}</span>
    </div>
  );
};

const ResponseCell = () => {
  const lead = useRecordContext<Lead>();
  if (!lead) return null;
  return <ResponseTime seconds={lead.first_response_seconds} />;
};

const ClaimCell = () => {
  const lead = useRecordContext<Lead>();
  if (!lead) return null;
  return (
    <span className="tabular-nums">{formatDuration(lead.claim_seconds)}</span>
  );
};

const OwnerCell = () => {
  const lead = useRecordContext<Lead>();
  if (!lead) return null;
  if (lead.sales_id == null) return <ClaimLeadButton lead={lead} />;
  return <span>{lead.sales_name ?? "–"}</span>;
};

export const LeadList = () => (
  <List
    filters={filters}
    sort={{ field: "created_at", order: "DESC" }}
    perPage={25}
  >
    <div className="mb-2">
      <InboxFilter />
    </div>
    <DataTable rowClick="show">
      <DataTable.Col source="created_at">
        <DateField source="created_at" showTime />
      </DataTable.Col>
      <DataTable.Col label="resources.leads.fields.name">
        <NameCell />
      </DataTable.Col>
      <DataTable.Col label="resources.leads.fields.source">
        <SourceCell />
      </DataTable.Col>
      <DataTable.Col label="resources.leads.fields.status">
        <StatusCell />
      </DataTable.Col>
      <DataTable.Col label="resources.leads.fields.first_response_seconds">
        <ResponseCell />
      </DataTable.Col>
      <DataTable.Col label="resources.leads.fields.claim_seconds">
        <ClaimCell />
      </DataTable.Col>
      <DataTable.Col label="resources.leads.fields.sales_id">
        <OwnerCell />
      </DataTable.Col>
    </DataTable>
  </List>
);
